import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import StaffLayout from "@/components/StaffLayout";
import {
  useListIssues,
  useCreateIssue,
  useUpdateIssue,
  useDeleteIssue,
  useListCategories,
  useListStatuses,
  useGetIssueUpvotes,
  getListIssuesQueryKey,
  getGetIssuesSummaryQueryKey,
  Issue,
  Status,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Clock, CheckCircle2, Plus, Columns, List, Edit, Trash2, Tag, ThumbsUp } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const ISSUES_QUERY_KEY = getListIssuesQueryKey();
const SUMMARY_QUERY_KEY = getGetIssuesSummaryQueryKey();

const issueSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  categoryId: z.number().int().positive("Category is required"),
  statusId: z.number().int().positive("Status is required"),
  // affectedCount is deliberately absent: it is owned by public upvotes, and a
  // staff edit must never overwrite it with the value loaded into the dialog.
});

type IssueFormValues = z.infer<typeof issueSchema>;

function statusIconFor(key: string | undefined, sizeClass = "w-4 h-4") {
  switch (key) {
    case "new":
      return <AlertCircle className={`${sizeClass} text-destructive`} />;
    case "in_progress":
      return <Clock className={`${sizeClass} text-blue-500`} />;
    case "resolved":
      return <CheckCircle2 className={`${sizeClass} text-emerald-500`} />;
    default:
      return <Tag className={`${sizeClass} text-slate-400`} />;
  }
}

export default function StaffIssuesPage() {
  const { data: issues, isLoading } = useListIssues();
  const { data: categories } = useListCategories();
  const categoryById = new Map((categories ?? []).map((c) => [c.id, c]));
  const categoryNameFor = (id: number | null | undefined) =>
    id != null ? categoryById.get(id)?.name ?? "—" : "—";
  const { data: issueStatuses } = useListStatuses({ scope: "issues" });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<"kanban" | "table">("table");

  const createIssue = useCreateIssue();
  const updateIssue = useUpdateIssue();
  const deleteIssue = useDeleteIssue();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIssueId, setEditingIssueId] = useState<number | null>(null);
  const [upvoteViewIssueId, setUpvoteViewIssueId] = useState<number | null>(null);
  const { data: upvotes, isLoading: upvotesLoading } = useGetIssueUpvotes(upvoteViewIssueId ?? 0, {
    query: { enabled: upvoteViewIssueId !== null },
  });
  const upvoteViewIssue = upvoteViewIssueId !== null ? (issues ?? []).find((i) => i.id === upvoteViewIssueId) ?? null : null;

  const statusById = new Map<number, Status>((issueStatuses ?? []).map((s) => [s.id, s]));
  const activeStatuses = (issueStatuses ?? []).filter((s) => s.isActive);
  // Kanban columns: any status with backing issues OR currently active. Keeps
  // soft-deactivated statuses visible if existing issues still reference them.
  const referencedStatusIds = new Set((issues ?? []).map((i) => i.statusId));
  const kanbanStatuses = (issueStatuses ?? []).filter(
    (s) => s.isActive || referencedStatusIds.has(s.id),
  );
  const newStatusId = (issueStatuses ?? []).find((s) => s.key === "new")?.id;

  const form = useForm<IssueFormValues>({
    resolver: zodResolver(issueSchema),
    defaultValues: {
      title: "",
      description: "",
      categoryId: 0,
      statusId: 0,
    },
  });

  // Once statuses load, set the default form statusId to the "new" / Reported status.
  useEffect(() => {
    if (newStatusId && form.getValues("statusId") === 0) {
      form.setValue("statusId", newStatusId);
    }
  }, [newStatusId, form]);

  const openDialog = (issue?: Issue) => {
    if (issue) {
      setEditingIssueId(issue.id);
      form.reset({
        title: issue.title,
        description: issue.description,
        categoryId: issue.categoryId ?? 0,
        statusId: issue.statusId,
      });
    } else {
      setEditingIssueId(null);
      form.reset({
        title: "",
        description: "",
        categoryId: 0,
        statusId: newStatusId ?? 0,
      });
    }
    setIsDialogOpen(true);
  };

  const onSubmit = (data: IssueFormValues) => {
    if (editingIssueId) {
      updateIssue.mutate({ id: editingIssueId, data }, {
        onSuccess: () => {
          trackEvent("public_issue_updated", { statusId: data.statusId, categoryId: data.categoryId });
          toast({ title: "Issue updated successfully" });
          setIsDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY });
        },
      });
    } else {
      createIssue.mutate({ data }, {
        onSuccess: () => {
          trackEvent("public_issue_created", { statusId: data.statusId, categoryId: data.categoryId });
          toast({ title: "Issue created successfully" });
          setIsDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY });
        },
      });
    }
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this issue?")) {
      deleteIssue.mutate({ id }, {
        onSuccess: () => {
          toast({ title: "Issue deleted" });
          queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY });
        },
      });
    }
  };

  const handleStatusChange = (id: number, statusId: number) => {
    updateIssue.mutate({ id, data: { statusId } }, {
      onSuccess: () => {
        toast({ title: "Status updated" });
        queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY });
      },
    });
  };

  // Kanban columns built dynamically from active statuses.
  const kanbanColumns = kanbanStatuses.map((s) => ({
    status: s,
    issues: (issues ?? []).filter((i) => i.statusId === s.id),
  }));

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Public Issues Board</h1>
          <p className="text-slate-500 text-sm">Manage known MOTUS issues shown to the public.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-md">
            <Button
              variant="ghost" size="sm"
              className={`px-3 py-1 h-8 rounded-sm ${viewMode === 'table' ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900' : 'text-slate-500'}`}
              onClick={() => setViewMode('table')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" size="sm"
              className={`px-3 py-1 h-8 rounded-sm ${viewMode === 'kanban' ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900' : 'text-slate-500'}`}
              onClick={() => setViewMode('kanban')}
            >
              <Columns className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={() => openDialog()} className="bg-primary hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" /> New Issue
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : viewMode === "table" ? (
        <Card className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                <TableRow>
                  <TableHead className="w-[300px]">Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issues?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">No issues found.</TableCell>
                  </TableRow>
                ) : (
                  issues?.map((issue) => {
                    const currentStatus = statusById.get(issue.statusId);
                    const currentIsInactive = currentStatus && !currentStatus.isActive;
                    return (
                      <TableRow key={issue.id}>
                        <TableCell className="font-medium text-slate-900 dark:text-white">
                          {issue.title}
                          <div className="text-xs text-slate-500 font-normal mt-1 line-clamp-1">{issue.description}</div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{categoryNameFor(issue.categoryId)}</Badge></TableCell>
                        <TableCell>
                          <Select
                            value={String(issue.statusId)}
                            onValueChange={(val) => handleStatusChange(issue.id, Number(val))}
                          >
                            <SelectTrigger className="w-[160px] h-8 text-xs bg-transparent border-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {activeStatuses.map((s) => (
                                <SelectItem key={s.id} value={String(s.id)}>
                                  <span className="flex items-center gap-2">
                                    {statusIconFor(s.key, "w-3 h-3")} {s.label}
                                  </span>
                                </SelectItem>
                              ))}
                              {currentIsInactive && (
                                <SelectItem value={String(issue.statusId)}>
                                  <span className="flex items-center gap-2 text-slate-500">
                                    {statusIconFor(currentStatus.key, "w-3 h-3")} {currentStatus.label} (current, inactive)
                                  </span>
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{format(new Date(issue.createdAt), "MMM d, yyyy")}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            title={`View upvotes (${issue.affectedCount ?? 0})`}
                            onClick={() => setUpvoteViewIssueId(issue.id)}
                            className="text-slate-500 hover:text-primary"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openDialog(issue)} className="text-slate-500 hover:text-primary">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(issue.id)} className="text-slate-500 hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        /* Kanban View */
        <div
          className="grid grid-cols-1 md:grid-cols-[repeat(var(--kanban-cols),minmax(0,1fr))] gap-6"
          style={{ "--kanban-cols": Math.min(Math.max(kanbanColumns.length, 1), 4) } as React.CSSProperties}
        >
          {kanbanColumns.map((column) => (
            <div key={column.status.id} className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="font-bold flex items-center gap-2 text-slate-800 dark:text-slate-200">
                  {statusIconFor(column.status.key)} {column.status.label}
                </h3>
                <Badge variant="secondary">{column.issues.length}</Badge>
              </div>
              <div className="space-y-3">
                {column.issues.map((issue) => (
                  <Card key={issue.id} className="cursor-pointer hover:shadow-md transition-shadow border-slate-200" onClick={() => openDialog(issue)}>
                    <CardHeader className="p-3">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <Badge variant="outline" className="text-[10px]">{categoryNameFor(issue.categoryId)}</Badge>
                      </div>
                      <CardTitle className="text-sm">{issue.title}</CardTitle>
                    </CardHeader>
                  </Card>
                ))}
                {column.issues.length === 0 && <div className="text-center text-sm text-slate-500 py-4 border-2 border-dashed rounded-lg border-slate-200">No issues</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingIssueId ? "Edit Issue" : "Create New Issue"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. MCS-150 Form 500 Error" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Details about the issue..." className="min-h-[100px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : ""}
                        onValueChange={(val) => field.onChange(Number(val))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(categories ?? []).filter((c) => c.isActive).map((cat) => (
                            <SelectItem key={cat.id} value={String(cat.id)}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="statusId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : ""}
                        onValueChange={(val) => field.onChange(Number(val))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeStatuses.map((s) => (
                            <SelectItem key={s.id} value={String(s.id)}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-orange-600" disabled={createIssue.isPending || updateIssue.isPending}>
                  {editingIssueId ? "Save Changes" : "Create Issue"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Upvotes viewer dialog */}
      <Dialog open={upvoteViewIssueId !== null} onOpenChange={(open) => { if (!open) setUpvoteViewIssueId(null); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-primary" />
              Upvotes for: {upvoteViewIssue?.title ?? "Issue"}
            </DialogTitle>
          </DialogHeader>
          {upvotesLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !upvotes?.length ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No upvotes yet for this issue.
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 mb-3">{upvotes.length} upvote{upvotes.length !== 1 ? "s" : ""}</p>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead className="w-[110px]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upvotes.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="text-sm font-medium break-all">{u.email}</TableCell>
                        <TableCell className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words max-w-[200px]">
                          {u.comment ?? <span className="text-slate-400 italic">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {format(new Date(u.createdAt), "MMM d, yyyy")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpvoteViewIssueId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffLayout>
  );
}
